import { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, Clock, Quote } from "lucide-react";

export const INSIGHTS = [
    {
        slug: "pricing-grid-storage",
        category: "Energy Policy",
        date: "Sep 2025",
        read: "6 min read",
        title:
            "Why a £49–66/MWh band beats a £55/MWh point estimate for grid-scale storage.",
        dek:
            "A point estimate hides where the politics live. A band makes it impossible to look away from them. Notes from architecting the DESNZ techno-economic module.",
        body: [
            "When the UK Department for Energy Security & Net Zero (DESNZ) asked the three-university working group for grid-scale storage cost numbers, the temptation was to give them one. A single defensible figure, sourced cleanly, ready to drop into a brief. We pushed back, and the report ended up with a band: £49–66/MWh.",
            "The reason isn't analytical hedging. It's that a point estimate makes the political assumptions invisible. Cost of capital. Project lifetime. Capex curve slope. Market design. Each of these moves the headline number by ten quid or more, but only one of them sits in the model's caption. A band forces the question that actually matters in a policy room: which of these assumptions is the political decision, and who owns it?",
            "Our approach was deliberate. First, we synthesised the regulatory and TRL dynamics across five jurisdictions and briefed the leadership team. Second, we built a transparent LCOS-style techno-economic module — capex, opex, lifetime, round-trip efficiency, cost of capital, grid-service revenue. Third, we ran ~10 sensitivities and bounded the band by the two most policy-sensitive inputs, not the two most uncertain ones. Those are different things.",
            "The result was a number DESNZ analysts could stress-test in their own spreadsheets without needing us. That's the criterion I now apply to every techno-economic deliverable: can the client rebuild it? If not, you've handed over a black box, not a decision.",
        ],
        pullQuote:
            "A point estimate hides where the politics live. A band makes it impossible to look away from them.",
        related: ["chitosan-circularity", "from-lab-to-board"],
    },
    {
        slug: "chitosan-circularity",
        category: "Sustainability",
        date: "Jul 2025",
        read: "5 min read",
        title:
            "The hidden value of a 10%-by-weight byproduct: chitosan, glycerol, and the case for waste-first strategy.",
        dek:
            "A Forbes Global 2000 plastics-substitution thesis, an MSc thesis on glycerol valorisation, and one lesson: the most under-priced asset in your supply chain is probably the one your engineers call 'waste.'",
        body: [
            "Biodiesel produces glycerol as ~10% of its mass output. Crustacean processing produces chitin (and from it, chitosan) at industrial scale. Both are routinely treated as cost centres — disposed of, incinerated, or sold at depressed prices. Both are sitting on top of multi-billion-dollar conversion opportunities.",
            "I've watched this play out from two sides. At Imperial, I spent a year on Ni-Co hydroxide catalysts that turn crude glycerol into glyceraldehyde, glycolic acid and formate — high-value chemicals, with green hydrogen as a useful byproduct. The optimal composition we found (Ni₀.₀₇₅Co₀.₀₂₅(OH)₂) cut charge-transfer resistance ~10× versus pure Ni(OH)₂. The chemistry isn't speculative. The strategy gap is.",
            "At Atomalyst, the same logic applied to chitosan. A Forbes Global 2000 manufacturer was over-engineering polyolefin parts under mounting circularity regulation, with no credible bio-based alternative. We mapped the chitosan value chain — TRL by TRL, regulation by regulation — and found a path to a multi-billion-dollar market by 2033 with ~20% addressable cost savings. The frameworks got adopted into their procurement process.",
            "The pattern: a 'waste' tonnage on someone's balance sheet, regulatory pressure on a separate tonnage on another balance sheet, and a chemistry route nobody is being paid to operationalise. Strategy work is about getting those three tonnages on the same page.",
        ],
        pullQuote:
            "The most under-priced asset in your supply chain is probably the one your engineers call 'waste.'",
        related: ["pricing-grid-storage", "from-lab-to-board"],
    },
    {
        slug: "from-lab-to-board",
        category: "Strategy",
        date: "Jun 2025",
        read: "4 min read",
        title:
            "From bench to board: how to brief a CEO on a TRL-4 technology without losing either of you.",
        dek:
            "Three habits I borrowed from strategy consulting that made my lab work readable to the people who actually allocate capital — and one that I had to unlearn.",
        body: [
            "Lab notebooks and McKinsey decks share more DNA than either side admits. Both are trying to compress a messy reality into a single defensible conclusion that survives interrogation. The difference is the interrogator.",
            "Habit one: open with the answer. A thesis chapter starts with context and works toward a conclusion. A board paper does the opposite. When I rewrote my MSc abstract as the executive summary it could have been, the opening line became the catalyst composition and its kinetic advantage — not the global energy demand. The 47% projected rise in fossil fuel consumption is the closing slide, not the opening one.",
            "Habit two: own the band, not the point. Whether you're sizing a chitosan market in 2033 or quoting an Rct value at a chosen pH, the credible thing to say is what range you trust, why, and what would change it. Boards are paid to interrogate sensitivities; analysts who serve up only a midpoint are doing them a disservice.",
            "Habit three: be ruthless with the methodology section. Anything that doesn't change what the decision-maker should do today gets a footnote. Five-sigma rigour is a feature for the journal; for the boardroom, it's a tax on attention.",
            "The unlearn: the lab teaches you that ambiguity is the enemy. The boardroom teaches you that it isn't — false certainty is. A C-suite that's been burned by overconfident analysts will trust the analyst who says 'I don't know, but here's how I'd find out' more than the one who picks a number.",
        ],
        pullQuote:
            "Lab notebooks and McKinsey decks share more DNA than either side admits — they're both trying to compress a messy reality into one defensible conclusion.",
        related: ["pricing-grid-storage", "chitosan-circularity"],
    },
];

export const INSIGHTS_BY_SLUG = Object.fromEntries(
    INSIGHTS.map((p) => [p.slug, p])
);

export default function InsightPost() {
    const { slug } = useParams();
    const post = INSIGHTS_BY_SLUG[slug];

    useEffect(() => {
        if (post) {
            document.title = `${post.title} — Hanan Hakim`;
            window.scrollTo({ top: 0, behavior: "instant" });
        }
    }, [post]);

    if (!post) return <Navigate to="/" replace />;

    const related = (post.related || [])
        .map((s) => INSIGHTS_BY_SLUG[s])
        .filter(Boolean);

    return (
        <main
            data-testid={`insight-page-${post.slug}`}
            className="bg-bone text-ink"
        >
            <header className="border-b border-line">
                <div className="max-w-[1280px] mx-auto px-6 md:px-12 lg:px-20 h-16 md:h-20 flex items-center justify-between gap-6">
                    <Link
                        to="/#insights"
                        data-testid="insight-back-link"
                        className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] text-ink-soft hover:text-ink transition-colors"
                    >
                        <ArrowLeft size={13} /> All insights
                    </Link>
                    <Link to="/" className="font-serif text-xl tracking-tight">
                        Hanan <span className="italic text-sienna">Hakim</span>
                    </Link>
                    <Link
                        to="/#recruiters"
                        className="hidden sm:inline-flex items-center gap-2 border border-ink px-4 py-2 font-mono text-[11px] uppercase tracking-[0.2em] text-ink hover:bg-ink hover:text-bone transition-colors"
                    >
                        Get in touch <ArrowUpRight size={12} />
                    </Link>
                </div>
            </header>

            <article className="py-16 md:py-24">
                <div className="max-w-[860px] mx-auto px-6 md:px-12">
                    <motion.div
                        initial={{ opacity: 0, y: 18 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                    >
                        <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-sienna">
                            {post.category} · {post.date}
                        </p>
                        <h1
                            data-testid="insight-headline"
                            className="font-serif text-4xl md:text-5xl lg:text-[3.4rem] leading-[1.08] tracking-tight text-ink mt-6"
                        >
                            {post.title}
                        </h1>
                        <p className="mt-6 font-serif italic text-xl md:text-2xl text-ink-soft leading-relaxed">
                            {post.dek}
                        </p>
                        <div className="mt-8 pt-6 border-t border-line flex items-center justify-between gap-4 font-mono text-[10px] uppercase tracking-[0.22em] text-ink-soft">
                            <span>By Hanan Hakim</span>
                            <span className="inline-flex items-center gap-1.5">
                                <Clock size={11} /> {post.read}
                            </span>
                        </div>
                    </motion.div>

                    <div className="mt-12 space-y-6 font-sans text-[1.05rem] md:text-[1.12rem] leading-[1.78] text-ink-soft">
                        {post.body.map((p, i) => (
                            <p key={i}>{p}</p>
                        ))}
                    </div>

                    {post.pullQuote && (
                        <div className="mt-14 border-l-2 border-sienna pl-6 md:pl-10 py-2">
                            <Quote size={22} className="text-sienna" />
                            <blockquote className="mt-3 font-serif italic text-2xl md:text-[1.7rem] leading-[1.35] text-ink">
                                &ldquo;{post.pullQuote}&rdquo;
                            </blockquote>
                        </div>
                    )}
                </div>
            </article>

            {related.length > 0 && (
                <section className="border-t border-line py-16 md:py-20 bg-bone-soft">
                    <div className="max-w-[1280px] mx-auto px-6 md:px-12 lg:px-20">
                        <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-sienna">
                            Related insights
                        </p>
                        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
                            {related.map((r) => (
                                <Link
                                    key={r.slug}
                                    to={`/insights/${r.slug}`}
                                    className="group border-t border-ink pt-6"
                                >
                                    <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-ink-soft">
                                        {r.category} · {r.date}
                                    </p>
                                    <h3 className="mt-3 font-serif text-2xl md:text-3xl text-ink leading-[1.15] group-hover:text-sienna transition-colors">
                                        {r.title}
                                    </h3>
                                </Link>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            <footer className="bg-ink text-bone/60 py-10 border-t border-line-strong">
                <div className="max-w-[1280px] mx-auto px-6 md:px-12 lg:px-20 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                    <p className="font-serif text-xl text-bone">
                        Hanan <span className="italic text-sienna">Hakim</span>
                    </p>
                    <Link
                        to="/#insights"
                        className="font-mono text-[11px] uppercase tracking-[0.22em] hover:text-bone transition-colors"
                    >
                        ← Back to all insights
                    </Link>
                </div>
            </footer>
        </main>
    );
}
